import type { ReactNode } from "react";

import { cn } from "~/lib/utils";

export type ProjectTab = "overview" | "updates" | "tasks" | "automations" | "team" | "settings";

export const PROJECT_TAB_LABEL: Record<ProjectTab, string> = {
  overview: "Overview",
  updates: "Updates",
  tasks: "Tasks",
  automations: "Automations",
  team: "Team",
  settings: "Settings",
};

/**
 * Every tab the surface can render. Kept complete because `?tab=` still routes
 * to each one — hiding a tab from the strip is a navigation decision, not a
 * removal, and a deep link into Overview or Updates must keep resolving.
 */
export const PROJECT_TABS: readonly ProjectTab[] = [
  "overview",
  "updates",
  "tasks",
  "automations",
  "team",
  "settings",
];

/**
 * Whether an unknown value names a routable tab.
 *
 * Lives beside `PROJECT_TABS` so the route's `?tab=` validation and the strip
 * cannot drift: adding a tab to the list is the only edit either needs.
 */
export function isProjectTab(value: unknown): value is ProjectTab {
  return typeof value === "string" && PROJECT_TABS.includes(value as ProjectTab);
}

/**
 * What the strip actually offers.
 *
 * Overview and Updates are read surfaces that the project no longer opens on,
 * and Automations has a global home at `/automations` that shows the same runs
 * with more room. Leaving all five here made the strip a table of contents for
 * a page whose real content is the board.
 *
 * Settings is offered because it is now the only home for the project's
 * properties — they used to live in a details rail beside the board.
 */
const VISIBLE_PROJECT_TABS: readonly ProjectTab[] = ["tasks", "team", "settings"];

export interface CmmdProjectTabsProps {
  readonly activeTab: ProjectTab;
  readonly onTabChange: (tab: ProjectTab) => void;
  /** Page-specific filters / actions (Linear right cluster). */
  readonly trailing?: ReactNode;
}

/**
 * Linear-style project tab strip: pill badges on the left, trailing controls on
 * the right.
 */
export function CmmdProjectTabs(props: CmmdProjectTabsProps) {
  return (
    <div className="flex items-center gap-2 border-b border-border px-4 py-2">
      <div
        role="tablist"
        aria-label="Project sections"
        className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto"
      >
        {VISIBLE_PROJECT_TABS.map((tab) => {
          const selected = props.activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => props.onTabChange(tab)}
              className={cn(
                // Linear: every tab is a pill by default; states only change fill/border/text.
                "inline-flex h-7 shrink-0 items-center gap-1 rounded-full border px-3 text-sm font-medium transition-colors duration-150",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                selected
                  ? "border-transparent bg-muted text-foreground"
                  : "border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              {PROJECT_TAB_LABEL[tab]}
            </button>
          );
        })}
      </div>
      {props.trailing != null ? (
        <div className="flex shrink-0 items-center gap-1.5">{props.trailing}</div>
      ) : null}
    </div>
  );
}
